import { signal } from '@preact/signals';
import { isValidSlug, parseSlugFromPath } from './lib/slug';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const VALID_MEAL_TYPES = new Set<string>(['lunch', 'dinner', 'breakfast', 'snack']);

function isValidIsoDate(s: string): boolean {
  if (!ISO_DATE.test(s)) return false;
  const d = new Date(s + 'T00:00:00Z');
  return !isNaN(d.getTime()) && d.toISOString().startsWith(s);
}

export const path = signal<string>(window.location.pathname);

window.addEventListener('popstate', () => { path.value = window.location.pathname; });

export function navigate(to: string): void {
  history.pushState(null, '', to);
  path.value = to;
}

export function backOr(fallback: string): void {
  const sameOrigin = !!document.referrer && (() => {
    try { return new URL(document.referrer).origin === location.origin; } catch { return false; }
  })();
  if (history.length > 1 && sameOrigin) {
    history.back();
  } else {
    navigate(fallback);
  }
}

export type Route =
  | { name: 'plan'; weekId?: string }
  | { name: 'recipes'; slot?: { date: string; meal_type: string } }
  | { name: 'recipe-detail'; id: string }
  | { name: 'shopping'; weekId?: string }
  | { name: 'settings' }
  | { name: 'unknown' };

export function parseRoute(p: string): { slug: string | null; route: Route } {
  const slug = parseSlugFromPath(p);
  if (!slug || !isValidSlug(slug)) return { slug: null, route: { name: 'unknown' } };
  const rest = p.replace(/^\/h\/[^/]+/, '');
  const url = new URL(window.location.origin + p);
  if (rest === '' || rest === '/') return { slug, route: { name: 'recipes' } };
  if (rest === '/plan') return { slug, route: { name: 'plan' } };
  let m = /^\/plan\/([^/]+)/.exec(rest);
  if (m) return { slug, route: { name: 'plan', weekId: m[1]! } };
  m = /^\/recipes\/([^/]+)/.exec(rest);
  if (m) return { slug, route: { name: 'recipe-detail', id: m[1]! } };
  if (rest.startsWith('/recipes')) {
    const slotParam = url.searchParams.get('slot');
    if (slotParam) {
      const [date, mt] = slotParam.split(':');
      if (date && mt && isValidIsoDate(date) && VALID_MEAL_TYPES.has(mt)) {
        return { slug, route: { name: 'recipes', slot: { date, meal_type: mt } } };
      }
      return { slug, route: { name: 'recipes' } };
    }
    return { slug, route: { name: 'recipes' } };
  }
  m = /^\/shopping\/([^/]+)/.exec(rest);
  if (m) return { slug, route: { name: 'shopping', weekId: m[1]! } };
  if (rest.startsWith('/shopping')) return { slug, route: { name: 'shopping' } };
  if (rest.startsWith('/settings')) return { slug, route: { name: 'settings' } };
  return { slug, route: { name: 'unknown' } };
}
